import {
  ArrowUpRight,
  Lock,
  Share2,
  ThumbsDown,
  ThumbsUp,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import BackButton from "../components/BackButton";
import { useAuth } from "../context/AuthContext";
import api, { getImageUrl } from "../services/api";

const IssueDetail = () => {
  const { id } = useParams();
  const { isAuthenticated, user } = useAuth();

  const [issue, setIssue] = useState(null);
  const [error, setError] = useState("");
  const [commentText, setCommentText] = useState("");
  const [loading, setLoading] = useState(true);
  const [upvotes, setUpvotes] = useState(0);
  const [downvotes, setDownvotes] = useState(0);

  const fetchIssue = useCallback(async () => {
    try {
      const res = await api.get(`/issues/${id}`);

      setIssue(res.data);
      setUpvotes(res.data.upvotes?.length || 0);
      setDownvotes(res.data.downvotes?.length || 0);
    } catch (err) {
      console.error("Error fetching issue:", err);
      setError("Issue not found.");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchIssue();
  }, [fetchIssue]);

  const isSameArea = () => {
    const userDistrict = user?.permanentAddress?.district?.toLowerCase();
    const userConstituency =
      user?.permanentAddress?.mlaConstituency?.toLowerCase() ||
      user?.permanentAddress?.mpConstituency?.toLowerCase();

    return (
      issue &&
      user &&
      issue.constituency?.toLowerCase() === userConstituency &&
      issue.district?.toLowerCase() === userDistrict
    );
  };

  const handleVote = async (type) => {
    if (!isAuthenticated) {
      alert("Please log in to vote on this issue");
      return;
    }

    if (!isSameArea()) {
      alert("Only local voters can vote on this issue");
      return;
    }

    try {
      await api.post(`/issues/${id}/${type}`, {});
      fetchIssue();
    } catch (err) {
      console.error("Vote failed:", err);
    }
  };

  const handleCommentSubmit = async () => {
    if (!commentText.trim()) return;

    if (!isAuthenticated) {
      alert("Please log in to comment on this issue");
      return;
    }

    try {
      await api.post(`/issues/${id}/comments`, { text: commentText });
      setCommentText("");
      fetchIssue();
    } catch (err) {
      console.error("Comment failed:", err);
    }
  };

  if (loading) {
    return <div className="py-10 text-center text-gray-500">Loading...</div>;
  }

  if (error) {
    return <div className="py-10 text-center text-red-500">{error}</div>;
  }

  if (!issue) return null;

  return (
    <div className="mx-auto min-h-screen max-w-5xl bg-gradient-to-b from-orange-50 via-white to-green-50 px-4 py-12">
      <BackButton className="mb-6" />
      <h1 className="mb-2 text-3xl font-bold text-blue-800">{issue.title}</h1>

      <p className="mb-4 text-lg text-gray-700">{issue.description}</p>

      <div className="mb-6 flex items-center gap-4 text-sm text-gray-600">
        <p>
          <strong>
            {issue.state}, {issue.district}
          </strong>
        </p>

        <p className="rounded-full bg-yellow-500 px-2 py-1 text-xs font-medium text-white">
          {issue.status}
        </p>

        <p>{new Date(issue.createdAt).toLocaleDateString()}</p>
      </div>

      <p className="mb-4 text-sm text-gray-600">
        <strong>Raised By:</strong> {issue.raisedBy?.anonymousId || "Anonymous"}
      </p>

      <div className="mb-6 flex items-center gap-6">
        <button
          onClick={() => handleVote("upvote")}
          disabled={!isSameArea()}
          className={`flex items-center gap-1 ${
            isSameArea()
              ? "text-green-700 hover:text-green-900"
              : "cursor-not-allowed text-gray-400"
          }`}
        >
          <ThumbsUp size={20} /> {upvotes}
        </button>

        <button
          onClick={() => handleVote("downvote")}
          disabled={!isSameArea()}
          className={`flex items-center gap-1 ${
            isSameArea()
              ? "text-red-600 hover:text-red-800"
              : "cursor-not-allowed text-gray-400"
          }`}
        >
          <ThumbsDown size={20} /> {downvotes}
        </button>

        <button
          onClick={() => navigator.clipboard.writeText(window.location.href)}
          className="flex items-center gap-1 text-blue-600 hover:text-blue-800"
        >
          <Share2 size={20} /> Share
        </button>
      </div>

      {!isSameArea() && (
        <div className="mb-6 flex items-center gap-2 text-sm font-medium text-orange-600">
          <Lock size={16} /> This issue can only be voted by local verified voters.
        </div>
      )}

      {issue.video && (
        <div className="mb-6">
          <h3 className="mb-2 text-lg font-semibold">Attached Video:</h3>

          <video
            src={getImageUrl(issue.video)}
            controls
            className="max-w-full rounded-lg shadow-md"
          />
        </div>
      )}

      {issue.files?.length > 0 && (
        <div className="mb-8">
          <h3 className="mb-2 text-lg font-semibold">Attached Images:</h3>

          <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
            {issue.files.map((file, idx) => (
              <img
                key={idx}
                src={getImageUrl(file)}
                alt={`attachment-${idx}`}
                className="h-48 w-full rounded-lg object-cover shadow-md"
              />
            ))}
          </div>
        </div>
      )}

      <div className="mt-10">
        <h3 className="mb-4 text-xl font-semibold text-gray-800">
          Comments ({issue.comments?.length || 0})
        </h3>

        <div className="mb-6 flex gap-4">
          <input
            type="text"
            value={commentText}
            onChange={(e) => setCommentText(e.target.value)}
            placeholder="Write a comment..."
            className="flex-grow rounded border border-gray-300 px-4 py-2"
          />

          <button
            onClick={handleCommentSubmit}
            className="rounded bg-blue-600 px-4 py-2 text-white hover:bg-blue-700"
          >
            Post
          </button>
        </div>

        <div className="space-y-4">
          {issue.comments?.map((comment, idx) => (
            <div
              key={idx}
              className="rounded-lg border bg-white p-4 shadow-sm transition hover:shadow-md"
            >
              <div className="mb-1 flex items-center justify-between">
                <p className="font-semibold text-gray-700">
                  {comment.postedBy?.anonymousId || "Anonymous"}
                </p>

                <span className="text-xs text-gray-400">
                  {new Date(comment.date).toLocaleString()}
                </span>
              </div>

              <p className="mb-2 text-sm text-gray-600">{comment.text}</p>

              <div className="flex gap-4 text-sm text-gray-500">
                <button className="inline-flex items-center gap-1 hover:text-green-600">
                  <ThumbsUp size={14} />
                </button>

                <button className="inline-flex items-center gap-1 hover:text-red-600">
                  <ThumbsDown size={14} />
                </button>

                <button className="inline-flex items-center gap-1 hover:text-blue-600">
                  <ArrowUpRight size={14} /> Reply
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default IssueDetail;
